'use client'
import { type FC, useMemo } from 'react'
import { Box, type BoxProps } from '@chakra-ui/react'
import type { ProductLightDTO } from 'ecosystem'
import { Currency } from 'ecosystem'
import { getProductPrice, quantityDiscountSummary } from 'shared-utils'
import ParsedHtml from 'ui/common/ParsedHtml'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'
import { useVatMode } from '../../../common/hooks/useVatMode'
import { type QuantityDiscountTeaserOverrides, teaserContent } from './template'

/** Which store setting drives this instance. */
export type QuantityDiscountSurface = 'listItem' | 'pdp' | 'cart' | 'checkout'

export interface QuantityDiscountTeaserProps extends Omit<BoxProps, 'children'> {
  product: Pick<ProductLightDTO, 'name' | 'quantityDiscount'> &
    Parameters<typeof getProductPrice>[0]
  /**
   * Units on the line the ladder is scored against. `0` on a product card or an
   * untouched PDP, which is the right input — the teaser then advertises the
   * first breakpoint rather than claiming a discount nobody has earned.
   */
  quantity: number
  surface: QuantityDiscountSurface
}

/**
 * Merchant-authored nudge toward a product's volume tiers.
 *
 * Renders nothing at all when the product carries no ladder, the surface is
 * switched off in store settings, or the merchant blanked the copy for the
 * state the line is in — so it can be mounted unconditionally.
 *
 * The copy is an HTML template with `{{token}}` placeholders, the same
 * mechanism the MAP_LOCATOR popover uses: values are escaped before
 * substitution and `ParsedHtml` sanitises the assembled markup on the way out,
 * so a merchant can only inject markup they wrote themselves.
 */
export const QuantityDiscountTeaser: FC<QuantityDiscountTeaserProps> = ({
  product,
  quantity,
  surface,
  ...boxProps
}) => {
  const { quantityDiscountDisplay, currency } = useUIGlobalsContext()
  const { excludeVat } = useVatMode()
  // The store's label dictionary — `translations/<language>.json` merged into
  // `appConfig.labels` at build time. Without it the built-in copy would be
  // Swedish on every store.
  const overrides = useOverridesContext<keyof QuantityDiscountTeaserOverrides>()

  const html = useMemo(() => {
    const summary = quantityDiscountSummary(quantity, product.quantityDiscount)

    return teaserContent(
      quantityDiscountDisplay?.[surface],
      {
        summary,
        // The ladder is a percentage, so it reads the same off either side of
        // the VAT pair — but the money in the copy has to match the prices
        // beside it.
        unitPrice: getProductPrice(product, excludeVat),
        productName: product.name,
        currency: currency ?? Currency.SEK
      },
      overrides ?? {}
    )
  }, [currency, excludeVat, overrides, product, quantity, quantityDiscountDisplay, surface])

  if (!html) {
    return null
  }

  return (
    <Box
      className={`quantity-discount-teaser quantity-discount-teaser--${surface}`}
      fontSize="sm"
      sx={{
        p: { margin: 0 },
        /**
         * Chakra's `blackAlpha` scale rather than a `background.*` token: the
         * pill has to read on a white cart line *and* on a product card, which
         * is already `background.surface`, so any fixed surface token is
         * invisible on one of the two. Alpha tints whatever it sits on and is
         * present in every storefront theme (`extendTheme` keeps Chakra's
         * defaults). `background.mild` — the first thing tried here — is not a
         * token any theme defines, and an unknown colour token resolves to
         * nothing with no error anywhere.
         */
        '.qd-tier': {
          display: 'inline-block',
          mr: 1,
          mb: 1,
          px: 2,
          borderRadius: 'full',
          bg: 'blackAlpha.100',
          fontSize: 'xs',
          fontWeight: 'bold',
          whiteSpace: 'nowrap'
        }
      }}
      {...boxProps}
    >
      <ParsedHtml html={html} />
    </Box>
  )
}

export default QuantityDiscountTeaser

export * from './template'
export * from './placeholders'
